import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { CircularProgress, Box } from '@mui/material'
import { VehicleStateProvider } from './contexts/VehicleStateContext'

// Lazy load page components
// Auth Pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'))
const ChangePasswordPage = lazy(() => import('./pages/auth/ChangePasswordPage'))
const PasswordRecoveryPage = lazy(() => import('./pages/auth/PasswordRecoveryPage'))
const DashboardLayout = lazy(() => import('./pages/DashboardLayout'))

// Used Car Product Pages
const UsedCarPage = lazy(() => import('./pages/used-car/UsedCarPage'))
const VehicleDetailPage = lazy(() => import('./pages/used-car/VehicleDetailPage'))
const UserReportsPage = lazy(() => import('./pages/used-car/UserReportsPage'))
const VehicleTrendsPage = lazy(() => import('./pages/used-car/VehicleTrendsPage'))
const AllValuesPage = lazy(() => import('./pages/used-car/AllValuesPage'))
const MarketInsightsPage = lazy(() => import('./pages/used-car/MarketInsightsPage'))
const MarketReportPage = lazy(() => import('./pages/used-car/MarketReportPage'))
const MarketTrendsPage = lazy(() => import('./pages/used-car/MarketTrendsPage'))
const RunListsPage = lazy(() => import('./pages/used-car/RunListsPage'))
const SearchPage = lazy(() => import('./pages/used-car/SearchPage'))
const SpecsPage = lazy(() => import('./pages/used-car/SpecsPage'))
const VehicleHistoryPage = lazy(() => import('./pages/used-car/VehicleHistoryPage'))
const DemandIndexPage = lazy(() => import('./pages/used-car/DemandIndexPage'))
const VISPage = lazy(() => import('./pages/used-car/VISPage'))
const OtherServicesPage = lazy(() => import('./pages/used-car/OtherServicesPage'))

// New Car Product Pages
const NewCarHomePage = lazy(() => import('./pages/new-car/NewCarHomePage'))
const CalculatorsPage = lazy(() => import('./pages/new-car/CalculatorsPage'))
const ColorsPage = lazy(() => import('./pages/new-car/ColorsPage'))
const OptionsPage = lazy(() => import('./pages/new-car/OptionsPage'))
const QuotesPage = lazy(() => import('./pages/new-car/QuotesPage'))
const NewCarVehicleSpecsPage = lazy(() => import('./pages/new-car/VehicleSpecsPage'))

// Heavy Duty Trucks Pages
const TrucksHomePage = lazy(() => import('./pages/trucks/TrucksHomePage'))
const AbbreviationsPage = lazy(() => import('./pages/trucks/AbbreviationsPage'))
const MileageInfoPage = lazy(() => import('./pages/trucks/MileageInfoPage'))
const TrailerVinInfoPage = lazy(() => import('./pages/trucks/TrailerVinInfoPage'))
const TrucksUserReportsPage = lazy(() => import('./pages/trucks/UserReportsPage'))
const TrucksVehicleTrendsPage = lazy(() => import('./pages/trucks/VehicleTrendsPage'))

// Residual Values Pages
const ResidualsHomePage = lazy(() => import('./pages/residuals/ResidualsHomePage'))
const ChartsPage = lazy(() => import('./pages/residuals/ChartsPage'))
const DepreciationPage = lazy(() => import('./pages/residuals/DepreciationPage'))
const ValueRetentionPage = lazy(() => import('./pages/residuals/ValueRetentionPage'))

// PowerSports Pages
const PowerSportsHomePage = lazy(() => import('./pages/powersports/PowerSportsHomePage'))
const PowerSportsUserReportingPage = lazy(() => import('./pages/powersports/UserReportingPage'))
const PowerSportsVehicleTrendsPage = lazy(() => import('./pages/powersports/VehicleTrendsPage'))

// RV Pages
const RVHomePage = lazy(() => import('./pages/rv/RVHomePage'))
const RVUserReportingPage = lazy(() => import('./pages/rv/UserReportingPage'))

// CPI Pages
const CPIHomePage = lazy(() => import('./pages/cpi/CPIHomePage'))
const CPIUserReportingPage = lazy(() => import('./pages/cpi/UserReportingPage'))

// Loading component
const PageLoader = () => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <CircularProgress />
  </Box>
)

function App() {
  return (
    <VehicleStateProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/password-recovery" element={<PasswordRecoveryPage />} />
          
          {/* Dashboard routes */}
          <Route path="/" element={<DashboardLayout />}>
            <Route index element={<Navigate to="/used-car" replace />} />
            
            {/* Used Car Product Routes */}
            <Route path="used-car" element={<UsedCarPage />} />
            <Route path="used-car/vehicle/:id" element={<VehicleDetailPage />} />
            <Route path="used-car/all-values" element={<AllValuesPage />} />
            <Route path="used-car/demand-index" element={<DemandIndexPage />} />
            <Route path="used-car/market-insights" element={<MarketInsightsPage />} />
            <Route path="used-car/market-report" element={<MarketReportPage />} />
            <Route path="used-car/market-trends" element={<MarketTrendsPage />} />
            <Route path="used-car/other-services" element={<OtherServicesPage />} />
            <Route path="used-car/run-lists" element={<RunListsPage />} />
            <Route path="used-car/search" element={<SearchPage />} />
            <Route path="used-car/specs" element={<SpecsPage />} />
            <Route path="used-car/user-reports" element={<UserReportsPage />} />
            <Route path="used-car/vehicle-history" element={<VehicleHistoryPage />} />
            <Route path="used-car/vehicle-trends" element={<VehicleTrendsPage />} />
            <Route path="used-car/vis" element={<VISPage />} />

            {/* New Car Product Routes */}
            <Route path="new-car" element={<NewCarHomePage />} />
            <Route path="new-car/calculators" element={<CalculatorsPage />} />
            <Route path="new-car/colors" element={<ColorsPage />} />
            <Route path="new-car/options" element={<OptionsPage />} />
            <Route path="new-car/quotes" element={<QuotesPage />} />
            <Route path="new-car/vehicle-specs" element={<NewCarVehicleSpecsPage />} />

            {/* Heavy Duty Trucks Routes */}
            <Route path="trucks" element={<TrucksHomePage />} />
            <Route path="trucks/abbreviations" element={<AbbreviationsPage />} />
            <Route path="trucks/mileage-info" element={<MileageInfoPage />} />
            <Route path="trucks/trailer-vin-info" element={<TrailerVinInfoPage />} />
            <Route path="trucks/user-reports" element={<TrucksUserReportsPage />} />
            <Route path="trucks/vehicle-trends" element={<TrucksVehicleTrendsPage />} />

            {/* Residual Values Routes */}
            <Route path="residuals" element={<ResidualsHomePage />} />
            <Route path="residuals/charts" element={<ChartsPage />} />
            <Route path="residuals/depreciation" element={<DepreciationPage />} />
            <Route path="residuals/value-retention" element={<ValueRetentionPage />} />

            {/* PowerSports Routes */}
            <Route path="powersports" element={<PowerSportsHomePage />} />
            <Route path="powersports/user-reporting" element={<PowerSportsUserReportingPage />} />
            <Route path="powersports/vehicle-trends" element={<PowerSportsVehicleTrendsPage />} />

            {/* RV Routes */}
            <Route path="rv" element={<RVHomePage />} />
            <Route path="rv/user-reporting" element={<RVUserReportingPage />} />

            {/* CPI Routes */}
            <Route path="cpi" element={<CPIHomePage />} />
            <Route path="cpi/user-reporting" element={<CPIUserReportingPage />} />
          </Route>
          
          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </VehicleStateProvider>
  )
}

export default App
